import React, { useState, useEffect } from 'react'
import * as Sentry from "@sentry/react";
import FormAutoCompleteProvider from '@minohealth/common/components/input/extras/FormAutoCompleteProvider'
import SubmitButton from '@minohealth/common/components/button/SubmitButton'
import CustomInput from '@minohealth/common/components/input/CustomInput'
import CustomSelectInput from '@minohealth/common/components/input/CustomSelectInput'

import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useForm from '@minohealth/common/hooks/useForm'
import { genders, showErrorMessage } from '@minohealth/common/utils'
import useUserPatients from '@minohealth/common/hooks/useUserPatients'

import { IoMdClose } from 'react-icons/io'

import './chat-patient-form.css'
import { useChatContext } from '@/services/chat-provider';

const ChatType = {
  WITH_PATIENT_INFO: "with-patient-info",
  WITHOUT_PATIENT_INFO: "without-patient-info",
}

/**
  * 
  * @param {{ onClose: () => void, comment?: string }} props 
  * @returns 
  */
export default function ChatPatientForm({ onClose }) {
  const { useActiveChatSession, useUpdateChatSession } = useChatContext()

  const { t } = useTranslation();
  const { user } = useSelector((state) => state.app.datastate);
  const { data: activeChatSession } = useActiveChatSession()
  const {
    mutate: updateSession,
    isPending: isUpdatePending,
  } = useUpdateChatSession()
  const systemPatients = useUserPatients()
  const dispatch = useDispatch();

  const {
    formData: patientInfo,
    handleBlur,
    handleInputChange,
    resetForm,
    errors,
    touched,
    isFormValid
  } = useForm({
    name: {
      validations: [
        str => {
          if (isFieldNotEmpty({ str })) return
          return t("diagnose-form.patient-name-required", "Patient name is required")
        },
        str => {
          if ((/^[a-zA-Z\-. ]+$/).test(str)) return
          return t("diagnose-form.invalid-patient-name", "Invalid patient name")
        }
      ]
    },
    id: {
      validations: [
        str => {
          if (isFieldNotEmpty({ str })) return
          return t("diagnose-form.patient-id-required", "Patient ID is required")
        },
        str => {
          if ((/^.+$/).test(str)) return
          return t("diagnose-form.invalid-patient-id", "Invalid patient ID")
        }
      ]
    },
    age: {
      validations: [
        str => {
          if (isFieldNotEmpty({ str })) return
          return t("diagnose-form.age-required", "Patient age is required")
        },
        str => {
          if ((/^[1-9][0-9]*$/).test(str)) return
          return t("diagnose-form.invalid-age", "Invalid patient age")
        }
      ]
    },
    gender: {
      validations: [
        str => {
          if (isFieldNotEmpty({ str })) return
          return t("diagnose-form.gender-required", "Gender is required")
        },
        str => {
          if ((/^(male|female)$/).test(str.toLowerCase())) return
          return t("diagnose-form.invalid-gender", "Invalid gender")
        }
      ]
    }
  })

  function isFieldNotEmpty({ str }) {
    return (!(str == null || (/^\s*$/).test(str)))
  }

  /**
    * 
    * @param {React.FormEvent<HTMLFormElement>} e 
    */
  function handleSubmitPatientForm(e) {
    e.preventDefault()
    if (!isFormValid) return
    if (isUpdatePending) return

    patientInfo.id = patientInfo.id.toUpperCase()
    updateSession({
      sessionId: activeChatSession._id,
      sessionInfo: { patientInfo }
    }, {
      onSuccess() {
        resetForm()
        onClose?.()
      },
      onError(error) {
        Sentry.captureException(error, {
          extra: {
            context: 'ChatPatientForm - updateSession patient info',
            chatSessionId: activeChatSession?._id || 'undefined',
            userId: user?._id || 'undefined',
            patientName: patientInfo?.name || 'undefined',
            patientId: patientInfo?.id || 'undefined',
            patientAge: patientInfo?.age || 'undefined',
            patientGender: patientInfo?.gender || 'undefined'
          }
        });
        showErrorMessage(error?.message ?? "Failed to update patient information")
      }
    })
  }

  return (
    <form
      method="dialog"
      className='chat-patient-form'
      onSubmit={handleSubmitPatientForm}
    >
      <h2>Patient information</h2>

      <div className="chat-patient-form__form-fields">
        {/* <div 
          className="chat-patient-form__patient-info-toggle-field catch-focus" 
          data-checked={shouldTakePatientInfo} 
        >
          <label htmlFor={patientInfoCheckboxId} >
            <FaPlus />
            <span>Include patient info</span>
          </label>
          <input
            type="checkbox"
            id={patientInfoCheckboxId}
            className="sr-only"
            checked={shouldTakePatientInfo}
            onChange={ev => setShouldTakePatientInfo(prev => !prev)}
          />
        </div> */}

      </div>

      <fieldset className="chat-patient-form__patient-info-fieldset">
        <legend className="sr-only">Patient info</legend>

        <FormAutoCompleteProvider
          data={systemPatients}
          displayedKeys={["patientName", "patientId"]}
        >
          <CustomInput
            name="name"
            placeholder={t("diagnose-form.patient-name", "Patient Name")}
            value={patientInfo?.name ?? ""}
            onChange={handleInputChange("name")}
            errorMsg={touched.name && errors.name}
            onBlur={handleBlur("name")}
            autoCompleteKey="patientName"
            containerProps={{ className: "dark-variant" }}
          />
          <CustomInput
            name="id"
            placeholder={t("diagnose-form.patient-id", "Patient ID")}
            value={patientInfo?.id ?? ""}
            onChange={handleInputChange("id")}
            errorMsg={touched.id && errors.id}
            onBlur={handleBlur("id")}
            autoCompleteKey="patientId"
            containerProps={{ className: "dark-variant" }}
          />
          <CustomSelectInput
            name="gender"
            options={genders}
            value={patientInfo?.gender}
            onChangeOption={handleInputChange("gender")}
            errorMsg={touched.gender && errors.gender}
            onBlur={handleBlur("gender")}
            autoCompleteKey="gender"
            containerProps={{ className: "dark-variant" }}
          />
          <CustomInput
            name="age"
            placeholder={t("diagnose-form.patient-age", "Patient Age")}
            value={patientInfo?.age ?? ""}
            onChange={handleInputChange("age")}
            errorMsg={touched.age && errors.age}
            onBlur={handleBlur("age")}
            containerProps={{ className: "dark-variant" }}
            shouldShowAutoComplete={false}
          />
        </FormAutoCompleteProvider>
      </fieldset>

      <SubmitButton
        isLoading={isUpdatePending}
        disabled={!isFormValid}
        style={{
          marginBottom: "0em"
        }}
        data-guide-diagnose-btn
      >Add patient</SubmitButton>

      <button
        className="chat-patient-form__close-btn"
        type="button"
        onClick={e => {
          if (onClose == null) return
          onClose()
        }}
      >
        <IoMdClose size={24} />
      </button>
    </form>
  )
}
